import { forwardRef, type ComponentPropsWithoutRef, type ElementType } from 'react';

import { cx } from '../internal/cx.js';

export type TextSize = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
export type TextWeight = 'regular' | 'medium' | 'bold';
export type TextColor = 'default' | 'muted' | 'success' | 'warning' | 'danger' | 'info';
export type TextAlign = 'left' | 'center' | 'right';

export type TextProps = ComponentPropsWithoutRef<'span'> & {
  /** Polymorphic element tag. */
  as?: Extract<ElementType, 'span' | 'div' | 'p' | 'label'>;
  size?: TextSize;
  weight?: TextWeight;
  color?: TextColor;
  align?: TextAlign;
  truncate?: boolean;
};

/** Body text primitive with semantic size and color tokens. Requires `@import '@nrx/ui/theme.css'`. */
export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  {
    as,
    className,
    size = '3',
    weight = 'regular',
    color = 'default',
    align,
    truncate = false,
    ...rest
  },
  ref,
): React.JSX.Element {
  const Component = as ?? 'span';

  return (
    <Component
      ref={ref as never}
      className={cx(
        'nrx-text',
        `nrx-text--size-${size}`,
        `nrx-text--${weight}`,
        `nrx-text--color-${color}`,
        align && `nrx-text--align-${align}`,
        truncate && 'nrx-text--truncate',
        className,
      )}
      {...rest}
    />
  );
});
